// Shared text-treatment reactivity helpers — pure functions, no
// Stimulus dependency. Per-template live-preview controllers import
// these instead of inlining the same DOM-manipulation logic.
//
// Mirrors of these helpers exist in:
//   - Ruby:        Common::StyledTextDefaults + VideoEditor::StyledTextConfigBuilder
//   - TypeScript:  remotion-service/templates/src/shared/typography/styled_text.ts
// Keep all three in lockstep — drift means the live preview lies
// about what the renderer will produce.

// Resolves whether display text should render italic, given the type
// pair's default and the user's override. Mirror of resolveItalic in
// the TS + Ruby helpers.
export function resolveItalic(pairDefault, override) {
  switch (override) {
    case "italic":  return true
    case "upright": return false
    default:        return !!pairDefault
  }
}

// Applies WebkitTextStroke + paint-order to each target. Width clamped
// to [0, maxWidth]; non-numeric or non-positive width clears the
// stroke. Empty / null targets skipped.
//
// Targets accept two shapes (mix freely in one call):
//   1. HTMLElement — uniform stroke, the user's saved width applied 1:1.
//   2. { element, basePx, skip } — proportional stroke. The largest
//      basePx in the array is treated as the "hero"; each element's
//      effective stroke is `width × (basePx / heroBasePx)`. So a
//      4px-at-hero stroke on a 56px hero becomes 1px on a 14px caption
//      — the visual weight stays consistent across font sizes.
//      `skip: true` clears stroke on that element regardless of width
//      (use for elements with their own coloured background — pills,
//      badges — where stroke would noise up the chrome).
//
// Mixed-shape calls fall back to uniform mode (no proportional scaling)
// when no basePx is provided. Bare-element callers (most controllers
// before this helper grew) keep their existing behaviour.
//
// Usage (proportional):
//   applyTextStrokeToElements(
//     [
//       { element: this.heroTarget,    basePx: 56 },
//       { element: this.captionTarget, basePx: 14 },
//       { element: this.pillTarget,    skip: true },  // has bg
//     ],
//     { width, color },
//   )
//
// Usage (uniform — backwards compat):
//   applyTextStrokeToElements([this.titleTarget, this.authorTarget], { width, color })
export function applyTextStrokeToElements(targets, { width, color, maxWidth = 20 } = {}) {
  const w = Math.max(0, Math.min(maxWidth, parseFloat(width) || 0))
  const pairs = targets
    .map((t) => (t && typeof t === "object" && "element" in t ? t : { element: t }))
    .filter((p) => p.element)
  // Hero = largest basePx in the call. Falls back to uniform mode when
  // no caller provided basePx.
  const basePxs = pairs.map((p) => p.basePx).filter((n) => Number.isFinite(n) && n > 0)
  const heroBasePx = basePxs.length > 0 ? Math.max(...basePxs) : null

  for (const { element: el, basePx, skip } of pairs) {
    if (skip) {
      el.style.webkitTextStroke = ""
      el.style.paintOrder = ""
      continue
    }
    const effectiveWidth =
      heroBasePx && Number.isFinite(basePx) && basePx > 0
        ? w * (basePx / heroBasePx)
        : w
    if (effectiveWidth > 0) {
      el.style.webkitTextStroke = `${effectiveWidth.toFixed(2)}px ${color || "#000000"}`
      el.style.paintOrder = "stroke fill"
    } else {
      el.style.webkitTextStroke = ""
      el.style.paintOrder = ""
    }
  }
}

// Multiplies each element's base size by the user's scale and writes
// `fontSize: ${px}px` inline. `pairs` is an array of `{element, basePx}`
// — each per-template controller knows its own base sizes (renderer
// pixel values; preview-scale conversion is the controller's job),
// so this helper takes them per-call rather than via a shared
// registry.
//
// Non-finite or non-positive scale is a no-op.
export function applyFontSizeScale(pairs, scale) {
  const s = parseFloat(scale)
  if (!Number.isFinite(s) || s <= 0) return
  for (const { element, basePx } of pairs) {
    if (!element) continue
    element.style.fontSize = `${Math.round(basePx * s)}px`
  }
}

// Toggles a default drop shadow on a list of text elements. Used by
// every kit-template live-preview controller's `case "text_shadow":`
// branch so the wizard's drop-shadow checkbox visibly affects the
// preview text. Per CLAUDE.md's kit-wide style standard.
//
// `enabled` may be a boolean OR a string ("1"/"0"/"true"/"false"/"on")
// since the source is a checkbox event whose `target.checked` is the
// canonical signal but per-controller handlers occasionally pass the
// raw value too. Anything not strictly truthy clears the shadow.
//
// `shadow` accepts a custom CSS value; defaults to a standard 2-12-0.6
// rgba which mirrors the renderer's textShadow constant.
export function applyTextShadowToElements(elements, enabled, shadow = "0 2px 12px rgba(0,0,0,0.6)") {
  const on = enabled === true ||
             enabled === "true" ||
             enabled === "on" ||
             enabled === "1" ||
             enabled === 1
  for (const el of elements) {
    if (!el) continue
    el.style.textShadow = on ? shadow : "none"
  }
}

// Applies a TypePairSpec to display + body element groups, with an
// italic override applied to display only. Mirrors the buildTextStyle
// logic in TS but writes inline styles. Replaces the ~60-line
// `applyTypography` blocks each per-template controller used to
// hand-roll.
//
// Elements in the body group can opt out of the pair's body
// textTransform with `data-keep-uppercase="true"` (used for badges /
// phase strips that always render uppercase regardless of pair).
export function applyTypographyToElements({ display = [], body = [] }, spec, italicOverride) {
  if (!spec) return
  const displayItalic = resolveItalic(spec.displayItalic, italicOverride)

  for (const el of display) {
    if (!el) continue
    el.style.fontFamily = `'${spec.displayFont}', serif`
    el.style.fontWeight = String(spec.displayWeight ?? 500)
    el.style.fontStyle = displayItalic ? "italic" : "normal"
    el.style.textTransform = spec.displayTextTransform ?? "none"
    el.style.letterSpacing = spec.displayLetterSpacing ?? ""
  }
  for (const el of body) {
    if (!el) continue
    el.style.fontFamily = `'${spec.bodyFont}', sans-serif`
    el.style.fontWeight = String(spec.bodyWeight ?? 400)
    el.style.fontStyle = spec.bodyItalic ? "italic" : "normal"
    if (el.dataset.keepUppercase === "true") {
      el.style.textTransform = "uppercase"
    } else {
      el.style.textTransform = spec.bodyTextTransform ?? "none"
    }
    el.style.letterSpacing = spec.bodyLetterSpacing ?? ""
  }
}
